import React, { useState, useCallback, useEffect, useRef, useDeferredValue } from "react"
import { Badge } from "antd"
import { CaretRightOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { ExportMapVariable } from "src/types/exportMap.types"
import { useExportMapStore } from "stores/exportMapStore"
import { validateVariableCode, hasOriginalCode } from "./utils/exportMapMerger"
import ExportMapCategoryRow from "./ExportMapCategoryRow.component"

interface ExportMapVariableCardProps {
  variable: ExportMapVariable
}

/**
 * Card component for displaying and editing a single variable's code.
 * Shows variable type, code input, and expandable categories.
 * Memoized to prevent re-renders when other variables change.
 */
const ExportMapVariableCard: React.FC<ExportMapVariableCardProps> = React.memo(({
  variable,
}) => {
  const updateVariableCode = useExportMapStore((state) => state.updateVariableCode)
  const updateMaxMultiCode = useExportMapStore((state) => state.updateMaxMultiCode)
  const isDuplicateCode = useExportMapStore((state) => state.isDuplicateCode)
  const [isExpanded, setIsExpanded] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [localCode, setLocalCode] = useState(variable.variableCode)
  const [localMaxMultiCode, setLocalMaxMultiCode] = useState(
    variable.maxMultiCode !== undefined ? String(variable.maxMultiCode) : ""
  )
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const validationDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const maxMultiDebounceRef = useRef<NodeJS.Timeout | null>(null)

  // Use deferred value for validation errors to keep input responsive
  const deferredErrors = useDeferredValue(validationErrors)

  // Sync local state when variable changes from external source (e.g., reset, import)
  useEffect(() => {
    setLocalCode(variable.variableCode)
  }, [variable.variableCode])

  // Sync max-multi code when variable changes from external source
  useEffect(() => {
    setLocalMaxMultiCode(
      variable.maxMultiCode !== undefined ? String(variable.maxMultiCode) : ""
    )
  }, [variable.maxMultiCode])

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (validationDebounceRef.current) clearTimeout(validationDebounceRef.current)
      if (maxMultiDebounceRef.current) clearTimeout(maxMultiDebounceRef.current)
    }
  }, [])

  const isOriginal = hasOriginalCode(variable)
  const hasCategories = variable.categories && variable.categories.length > 0

  /**
   * Validate code - debounced to avoid expensive checks on every keystroke
   */
  const validateCode = useCallback(
    (code: string) => {
      const errors = validateVariableCode(code)
      if (code.trim() && isDuplicateCode(code, variable.fullLabel)) {
        errors.push("Duplicate variable code")
      }
      setValidationErrors(errors)
    },
    [isDuplicateCode, variable.fullLabel]
  )

  /**
   * Handle code change - update local state immediately, debounce everything else
   */
  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newCode = e.target.value

      // Update local state immediately for responsive typing
      setLocalCode(newCode)

      // Debounce validation (runs after 150ms of no typing)
      if (validationDebounceRef.current) {
        clearTimeout(validationDebounceRef.current)
      }
      validationDebounceRef.current = setTimeout(() => {
        validateCode(newCode)
      }, 150)

      // Debounce store update (runs after 300ms of no typing)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        updateVariableCode(variable.fullLabel, newCode)
      }, 300)
    },
    [validateCode, variable.fullLabel, updateVariableCode]
  )

  /**
   * Sync to store immediately on blur (in case user tabs away before debounce fires)
   */
  const handleCodeBlur = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    if (validationDebounceRef.current) {
      clearTimeout(validationDebounceRef.current)
    }
    // Validate and update store immediately on blur
    validateCode(localCode)
    if (localCode !== variable.variableCode) {
      updateVariableCode(variable.fullLabel, localCode)
    }
  }, [localCode, variable.variableCode, variable.fullLabel, updateVariableCode, validateCode])

  /**
   * Handle max-multi code change — debounced store update
   */
  const handleMaxMultiCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVal = e.target.value
      setLocalMaxMultiCode(newVal)

      if (maxMultiDebounceRef.current) clearTimeout(maxMultiDebounceRef.current)
      maxMultiDebounceRef.current = setTimeout(() => {
        const parsed = parseInt(newVal, 10)
        updateMaxMultiCode(variable.fullLabel, isNaN(parsed) ? undefined : parsed)
      }, 300)
    },
    [variable.fullLabel, updateMaxMultiCode]
  )

  const handleMaxMultiCodeBlur = useCallback(() => {
    if (maxMultiDebounceRef.current) clearTimeout(maxMultiDebounceRef.current)
    const parsed = parseInt(localMaxMultiCode, 10)
    updateMaxMultiCode(variable.fullLabel, isNaN(parsed) ? undefined : parsed)
  }, [localMaxMultiCode, variable.fullLabel, updateMaxMultiCode])

  /**
   * Get the variable label (last part of path)
   */
  const variableLabel =
    variable.path[variable.path.length - 1] || variable.fullLabel

  /**
   * Get type badge color
   */
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "CAT":
        return "#1890ff"
      case "BOOL":
        return "#52c41a"
      case "INT":
        return "#faad14"
      case "REAL":
        return "#eb2f96"
      case "TEXT":
        return "#722ed1"
      default:
        return "#666"
    }
  }

  return (
    <div
      className={`border rounded p-2 mb-1 border-gray-200 bg-white ${
        deferredErrors.length > 0 ? "border-red-300" : ""
      }`}
    >
      {/* Header row: Label + Type + Code Input */}
      <div className="flex items-center gap-2">
        {/* Expand toggle for categories */}
        {hasCategories && (
          <button
            className="p-1 hover:bg-gray-100 rounded"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <CaretRightOutlined
              rotate={isExpanded ? 90 : 0}
              className="text-gray-400"
            />
          </button>
        )}

        {/* Variable Label */}
        <div className="flex-1 min-w-0">
          <span className="font-medium truncate block">{variableLabel}</span>
        </div>

        {/* Type Badge */}
        <Badge
          count={variable.type}
          style={{
            backgroundColor: getTypeBadgeColor(variable.type),
            fontSize: 11,
          }}
        />

        {/* Code Input */}
        <div className="flex items-center gap-1">
          <span className="text-gray-500 text-sm">Code:</span>
          <input
            type="text"
            value={localCode}
            onChange={handleCodeChange}
            onBlur={handleCodeBlur}
            className={`w-40 px-2 py-1 text-sm border rounded transition-colors focus:outline-none focus:ring-1 ${
              deferredErrors.length > 0
                ? "border-red-400 focus:border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:border-blue-400 focus:ring-blue-200"
            }`}
          />

          {/* Original/Auto indicator */}
          <Badge
            count={isOriginal ? "ORIG" : "AUTO"}
            style={{
              backgroundColor: isOriginal ? "#52c41a" : "#faad14",
              fontSize: 9,
              padding: "0 4px",
            }}
          />
        </div>

        {/* Max-multi code input for BOOL fields */}
        {variable.type === "BOOL" && variable.maxMultiCode !== undefined && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-xs">Value:</span>
            <input
              type="text"
              value={localMaxMultiCode}
              onChange={handleMaxMultiCodeChange}
              onBlur={handleMaxMultiCodeBlur}
              className="w-16 px-1 py-0.5 text-sm text-center border border-gray-300 rounded focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
            />
          </div>
        )}
      </div>

      {/* Validation errors */}
      {deferredErrors.length > 0 && (
        <div className="mt-2 text-red-500 text-sm flex items-center gap-1">
          <InfoCircleOutlined />
          {deferredErrors[0]}
        </div>
      )}

      {/* Type info for non-categorical */}
      {!hasCategories && (
        <div className="mt-1 text-gray-400 text-xs">
          ({variable.type} - no categories)
        </div>
      )}

      {/* Categories section */}
      {hasCategories && isExpanded && (
        <div className="mt-2 pl-4 border-l border-gray-200">
          {variable.categories!.map((category) => (
            <ExportMapCategoryRow
              key={`${variable.fullLabel}-${category.label}`}
              variableLabel={variable.fullLabel}
              category={category}
            />
          ))}
        </div>
      )}
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if the variable actually changed
  const prev = prevProps.variable
  const next = nextProps.variable
  return (
    prev.fullLabel === next.fullLabel &&
    prev.variableCode === next.variableCode &&
    prev.type === next.type &&
    prev.categories === next.categories &&
    prev.maxMultiCode === next.maxMultiCode
  )
})

export default ExportMapVariableCard
